import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

import { ZenitEventApiService } from '../../../services/api/zenit-event-api.service';
import { ZenitEventParserService } from '../../../services/parser/zenit-event-parser.service';

import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { SearchActions } from '@pages/search';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { SearchAssistantEvent } from '@shared/analytics/ZENIT/models/events/search-assistant.model';
import { ZenitEventName } from '@shared/analytics/ZENIT/models/zenit-general.model';
import { ZenitEventError, ZenitEventSuccess } from '@shared/analytics/ZENIT/store/actions/zenit.actions';

@Injectable()
export class SearchAssistantEventEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly zenitEventParserService: ZenitEventParserService,
        private readonly zenitEventApiService: ZenitEventApiService,
        private readonly store: Store,
    ) {
    }

    public SearchAssistantSuccessEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SearchActions.GetProductsArraySearchAction),
            concatLatestFrom(() => this.store.select(getCmsConfigValue('enabled_zenit'))),
            filter(([_, enabledZenit]) => enabledZenit),
            map(([action]) => this.zenitEventParserService.getEventObject<SearchAssistantEvent>(
                ZenitEventName.SearchAssistant,
                EventFrom.search,
                {
                    responseTime: action.queryResults.responseTime,
                    totalHits: action.queryResults.totalResults,
                    responseCode: 200,
                    searchPass: action.queryResults.searchpass,
                    provider: action.queryResults.provider,
                },
            )),
            concatMap((searchAssistant) => this.zenitEventApiService.postEvent$(searchAssistant, 'search-assistant.input.v1')
                .pipe(
                    map(() => ZenitEventSuccess<SearchAssistantEvent>()({ eventData: searchAssistant })),
                    catchError((error) => of(ZenitEventError({ error }))),
                )),
        );
    });

    public SearchAssistantErrorEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SearchActions.GetProductsArraySearchErrorAction),
            concatLatestFrom(() => this.store.select(getCmsConfigValue('enabled_zenit'))),
            filter(([_, enabledZenit]) => enabledZenit),
            map(() => this.zenitEventParserService.getEventObject<SearchAssistantEvent>(
                ZenitEventName.SearchAssistant,
                EventFrom.search,
                {
                    responseCode: -1,
                },
            )),
            concatMap((searchAssistant) => this.zenitEventApiService.postEvent$(searchAssistant, 'search-assistant.input.v1')
                .pipe(
                    map(() => ZenitEventSuccess<SearchAssistantEvent>()({ eventData: searchAssistant })),
                    catchError((error) => of(ZenitEventError({ error }))),
                )),
        );
    });
}
